import React, { useState, useEffect } from 'react';
import {
  Settings,
  Building2,
  Plus,
  Trash2,
  RefreshCw,
  Save,
  GripVertical,
} from 'lucide-react';
import { useAppConfig, useUpdateAppConfig } from '@/hooks/useAppConfig';
import { usePageTour, WelcomeModal } from '@/walkthrough';
import { settingsPageTour } from '@/walkthrough/tourSteps';
import toast from 'react-hot-toast';

// ===========================================
// Default Company Types (fallback if not configured)
// ===========================================

const DEFAULT_COMPANY_TYPES = [
  'Insurance Agent',
  'Plumber',
  'Restoration Company',
  'Property Manager',
  'Real Estate Agent',
  'Contractor',
  'Adjuster',
  'Attorney',
  'Other',
];

// ===========================================
// Main Component
// ===========================================

export function SettingsPage() {
  const { data: config, isLoading: configLoading } = useAppConfig();
  const updateConfig = useUpdateAppConfig();
  const { showWelcomeModal, startTour, skipTour, globalOptOut } = usePageTour(settingsPageTour);

  // Company types state
  const [companyTypes, setCompanyTypes] = useState<string[]>([]);
  const [newType, setNewType] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize company types from config
  useEffect(() => {
    if (config?.companyTypes) {
      setCompanyTypes(config.companyTypes);
    } else {
      setCompanyTypes(DEFAULT_COMPANY_TYPES);
    }
  }, [config]);

  // Track changes
  useEffect(() => {
    const originalTypes = config?.companyTypes || DEFAULT_COMPANY_TYPES;
    const changed = JSON.stringify(companyTypes) !== JSON.stringify(originalTypes);
    setHasChanges(changed);
  }, [companyTypes, config]);

  const handleAddType = () => {
    const trimmed = newType.trim();
    if (!trimmed) {
      toast.error('Please enter a company type');
      return;
    }
    if (companyTypes.includes(trimmed)) {
      toast.error('This company type already exists');
      return;
    }
    setCompanyTypes([...companyTypes, trimmed]);
    setNewType('');
  };

  const handleRemoveType = (index: number) => {
    setCompanyTypes(companyTypes.filter((_, i) => i !== index));
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newTypes = [...companyTypes];
    [newTypes[index - 1], newTypes[index]] = [newTypes[index], newTypes[index - 1]];
    setCompanyTypes(newTypes);
  };

  const handleMoveDown = (index: number) => {
    if (index === companyTypes.length - 1) return;
    const newTypes = [...companyTypes];
    [newTypes[index], newTypes[index + 1]] = [newTypes[index + 1], newTypes[index]];
    setCompanyTypes(newTypes);
  };

  const handleSave = async () => {
    try {
      await updateConfig.mutateAsync({ companyTypes });
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    }
  };

  const handleReset = () => {
    setCompanyTypes(DEFAULT_COMPANY_TYPES);
  };

  if (configLoading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="animate-spin text-blue-600" size={32} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Settings size={28} />
            Admin Settings
          </h1>
          <p className="page-description">
            Configure application-wide settings and options.
          </p>
        </div>
        {hasChanges && (
          <button
            onClick={handleSave}
            disabled={updateConfig.isPending}
            data-tour="save-settings"
            className="btn btn-primary"
          >
            {updateConfig.isPending ? (
              <>
                <RefreshCw className="animate-spin" size={18} />
                Saving...
              </>
            ) : (
              <>
                <Save size={18} />
                Save Changes
              </>
            )}
          </button>
        )}
      </div>

      {/* Company Types Section */}
      <div data-tour="company-types-config" className="card">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="text-blue-600" size={24} />
          <h2 className="text-lg font-semibold text-gray-900">Company Types</h2>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Manage the list of company types available when creating or editing referral partners.
          Drag to reorder, or use the arrows.
        </p>

        {/* Add new type */}
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newType}
            onChange={(e) => setNewType(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddType()}
            placeholder="Enter new company type..."
            className="input flex-1"
          />
          <button
            onClick={handleAddType}
            className="btn btn-primary"
          >
            <Plus size={18} />
            Add
          </button>
        </div>

        {/* Company types list */}
        <div className="space-y-2">
          {companyTypes.map((type, index) => (
            <div
              key={`${type}-${index}`}
              className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg group"
            >
              <div className="flex flex-col gap-0.5">
                <button
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                  className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                  title="Move up"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </button>
                <button
                  onClick={() => handleMoveDown(index)}
                  disabled={index === companyTypes.length - 1}
                  className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                  title="Move down"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
              <GripVertical className="text-gray-400" size={18} />
              <span className="flex-1 font-medium text-gray-800">{type}</span>
              <button
                onClick={() => handleRemoveType(index)}
                className="p-1 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remove"
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))}
        </div>

        {companyTypes.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No company types configured. Add some above.
          </div>
        )}

        {/* Reset to defaults */}
        <div className="mt-4 pt-4 border-t flex justify-between items-center">
          <button
            onClick={handleReset}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Reset to defaults
          </button>
          <span className="text-sm text-gray-500">
            {companyTypes.length} type{companyTypes.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      <WelcomeModal
        pageName={settingsPageTour.pageName}
        onStartTour={startTour}
        onSkip={skipTour}
        onGlobalOptOut={globalOptOut}
        open={showWelcomeModal}
      />

      {/* Unsaved changes warning */}
      {hasChanges && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-800">
              You have unsaved changes
            </p>
            <p className="text-sm text-yellow-700">
              Click "Save Changes" to apply your modifications.
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={updateConfig.isPending}
            className="btn btn-primary"
          >
            <Save size={18} />
            Save
          </button>
        </div>
      )}
    </div>
  );
}
